import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Users } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Dropdown } from '@/components/ui/dropdown';

interface User {
    _id: string;
    username: string;
    fullName?: string;
    email?: string;
    user_type: string;
    is_active: boolean;
    domain?: string | null;
    deletionRequestedAt?: string | null;
    deletionScheduledFor?: string | null;
}

const emptyUser = {
    username: '',
    password: '',
    fullName: '',
    email: '',
    user_type: 'domain-admin',
    is_active: true,
    domain: '',
};
interface Domain {
    _id: string;
    slug: string;
    url: string;
    is_active: boolean;
}
const UserManager: React.FC = () => {
    const [domains, setDomains] = useState<Domain[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [form, setForm] = useState<any>(emptyUser);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [purgeId, setPurgeId] = useState<string | null>(null);
    const [restoringId, setRestoringId] = useState<string | null>(null);

    const fetchDomains = () => {
        // setLoading(true);
        axios
            .get(`${BASE_URL}/api/domain`)
            .then((res) => setDomains(res.data))
        // .finally(() => setLoading(false));
    };

    const fetchUsers = () => {
        setLoading(true);
        axios
            .get(`${BASE_URL}/api/users`)
            .then((res) => setUsers(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchUsers();
        fetchDomains()
    }, []);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (form.user_type === 'domain-admin' && !form.domain?.trim()) {
            setError('Domain is required for domain-admin');
            toast.error('Domain is required for domain-admin');
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/users`, form);
            setAddOpen(false);
            setForm(emptyUser);
            toast.success('User added successfully!');
            if (response.data) {
                setUsers(prev => [...prev, response.data]);
            } else {
                fetchUsers();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to add user';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (user: User) => {
        setEditId(user._id);
        setForm({
            ...user,
            password: '', // Don't preload password
        });
        setEditOpen(true);
        setError('');
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (form.user_type === 'domain-admin' && !form.domain?.trim()) {
            const errorMsg = 'Domain is required for domain-admin';
            setError(errorMsg);
            toast.error(errorMsg);
            return;
        }

        setSaving(true);
        const currentEditId = editId;
        try {
            const response = await axios.put(`${BASE_URL}/api/users/${editId}`, form);
            setEditOpen(false);
            setEditId(null);
            setForm(emptyUser);
            toast.success('User updated successfully!');
            if (response.data) {
                setUsers(prev => prev.map(user =>
                    user._id === currentEditId ? { ...user, ...response.data } : user
                ));
            } else {
                fetchUsers();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update user';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        const deletedUser = users.find(user => user._id === deleteId);
        // Optimistic removal
        setUsers(prev => prev.filter(user => user._id !== deleteId));
        setDeleteId(null);
        try {
            await axios.delete(`${BASE_URL}/api/users/${deleteId}`);
            toast.success('User deleted successfully!');
        } catch (err: any) {
            // Revert on error
            if (deletedUser) {
                setUsers(prev => [...prev, deletedUser]);
            }
            const errorMessage = err.response?.data?.message || 'Failed to delete user';
            toast.error(errorMessage);
        }
    };

    const handleToggleActive = async (userId: string, currentStatus: boolean) => {
        setTogglingId(userId);
        // Optimistic update
        setUsers(prev => prev.map(user =>
            user._id === userId ? { ...user, is_active: !currentStatus } : user
        ));
        try {
            await axios.put(
                `${BASE_URL}/api/users/${userId}/toggle-status`,
                { is_active: !currentStatus }
            );
            toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
        } catch (err: any) {
            // Revert on error
            setUsers(prev => prev.map(user =>
                user._id === userId ? { ...user, is_active: currentStatus } : user
            ));
            const errorMessage = err.response?.data?.message || 'Failed to update user status';
            toast.error(errorMessage);
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="page-title">User Management</h2>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                        <Button>Add User</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add New User</DialogTitle>
                            <DialogDescription>Create a new user account with username, password, and role assignment</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAdd} className="space-y-5">
                            <Input name="fullName" placeholder="Full Name" value={form.fullName} onChange={handleFormChange} />
                            <Input name="email" placeholder="Email" value={form.email} onChange={handleFormChange} />
                            <Input name="username" placeholder="Username" value={form.username} onChange={handleFormChange} required />
                            <PasswordInput name="password" placeholder="Password" value={form.password} onChange={handleFormChange} required />
                            <Dropdown
                                value={form.user_type}
                                options={[
                                    { value: 'super-admin', label: 'Super Admin' },
                                    { value: 'domain-admin', label: 'Domain Admin' },
                                ]}
                                onValueChange={(val) => setForm((prev: any) => ({ ...prev, user_type: val }))}
                            />
                            {form.user_type === 'domain-admin' && (
                                <Dropdown
                                    value={form.domain}
                                    options={domains.map(d => ({ value: d.slug, label: d.slug }))}
                                    onValueChange={(val) => setForm((prev: any) => ({ ...prev, domain: val }))}
                                    placeholder="Select Domain"
                                />
                            )}
                            {error && <div className="text-danger">{error}</div>}
                            <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div>
                {loading ? (
                    <PageLoader label="Loading users..." />
                ) : users.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Users className="w-full h-full" />
                        </div>
                        <h3 className="empty-state-title">No users found</h3>
                        <p className="empty-state-description">Add your first user to get started</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead>Full Name</TableHead>
                                <TableHead>Username</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>User Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Domain</TableHead>
                                <TableHead className="w-24">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow 
                                    key={user._id} 
                                    className={`hover:bg-muted/50 transition-colors ${user.is_active ? 'bg-card' : 'bg-muted/50'}`}
                                >
                                    <TableCell className={`font-medium ${user.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {user.fullName || '-'}
                                    </TableCell>
                                    <TableCell className={`${user.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {user.username}
                                    </TableCell>
                                    <TableCell className={`${user.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {user.email || '-'}
                                    </TableCell>
                                    <TableCell className={`text-sm ${user.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {user.user_type}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={user.is_active}
                                                onCheckedChange={() => handleToggleActive(user._id, user.is_active)}
                                                disabled={togglingId === user._id || !!user.deletionRequestedAt}
                                            />
                                            <span className={user.is_active ? 'status-label-active' : 'status-label-inactive'}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {user.deletionScheduledFor && (
                                            <div className="mt-1 text-xs font-medium text-destructive">
                                                Deletion pending · {new Date(user.deletionScheduledFor).toLocaleDateString()}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className={`${user.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {user.domain || '-'}
                                    </TableCell>
                                    <TableCell className="flex gap-2">
                                        {user.deletionRequestedAt && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    loading={restoringId === user._id}
                                                    onClick={async () => {
                                                        setRestoringId(user._id);
                                                        try {
                                                            await axios.post(`${BASE_URL}/api/users/${user._id}/cancel-deletion`);
                                                            toast.success('Deletion cancelled — account restored');
                                                            fetchUsers();
                                                        } catch (err: any) {
                                                            toast.error(err.response?.data?.message || 'Failed to cancel deletion');
                                                        } finally {
                                                            setRestoringId(null);
                                                        }
                                                    }}
                                                >
                                                    Restore
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => setPurgeId(user._id)}
                                                >
                                                    Purge now
                                                </Button>
                                            </>
                                        )}
                                        <Button 
                                            variant="outline" 
                                            size="icon-sm" 
                                            onClick={() => handleEdit(user)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </Button>
                                        <Button 
                                            variant="destructive" 
                                            size="icon-sm" 
                                            onClick={() => setDeleteId(user._id)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit User</DialogTitle>
                        <DialogDescription>Update user information including username, email, password, and role assignment</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-5">
                        <Input name="fullName" placeholder="Full Name" value={form.fullName} onChange={handleFormChange} />
                        <Input name="email" placeholder="Email" value={form.email} onChange={handleFormChange} />
                        <Input name="username" placeholder="Username" value={form.username} onChange={handleFormChange} required />
                        <PasswordInput name="password" placeholder="New Password (leave blank to keep unchanged)" value={form.password} onChange={handleFormChange} />
                        <Dropdown
                            value={form.user_type}
                            options={[
                                { value: 'super-admin', label: 'Super Admin' },
                                { value: 'domain-admin', label: 'Domain Admin' },
                            ]}
                            onValueChange={(val) => setForm((prev: any) => ({ ...prev, user_type: val }))}
                        />
                        {form.user_type === 'domain-admin' && (
                            <Dropdown
                                value={form.domain}
                                options={domains.map(d => ({ value: d.slug, label: d.slug }))}
                                onValueChange={(val) => setForm((prev: any) => ({ ...prev, domain: val }))}
                                placeholder="Select Domain"
                            />
                        )}
                        {error && <div className="text-danger">{error}</div>}
                        <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
                    </form>
                </DialogContent>
            </Dialog>


            {/* Delete Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete User"
                description="Are you sure you want to delete this user? This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />

            {/* Purge-now confirmation (skips the remaining grace period) */}
            <ConfirmDialog
                open={!!purgeId}
                onOpenChange={(open) => !open && setPurgeId(null)}
                title="Permanently delete this account now?"
                description="This skips the remaining grace period and immediately erases the user, their domain, and all content. Billing and audit records are retained. This cannot be undone."
                confirmLabel="Purge now"
                onConfirm={async () => {
                    if (!purgeId) return;
                    try {
                        await axios.post(`${BASE_URL}/api/users/${purgeId}/purge-now`);
                        toast.success('Account permanently deleted');
                        setUsers(prev => prev.filter(u => u._id !== purgeId));
                    } catch (err: any) {
                        toast.error(err.response?.data?.message || 'Failed to purge account');
                    } finally {
                        setPurgeId(null);
                    }
                }}
            />
        </div>
    );
};

export default UserManager;
